import React, { useState } from "react";
import { ArrowRight } from "lucide-react";
import { InProgressSpinner } from "../shared/InProgressSpinner";

interface MagicLinkSignInFormProps {
  onSend: (email: string) => Promise<void>;
  disabled?: boolean;
}

export function MagicLinkSignInForm({
  onSend,
  disabled = false,
}: MagicLinkSignInFormProps) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">(
    "idle",
  );
  const [message, setMessage] = useState("");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setStatus("error");
      setMessage("Enter an email.");
      return;
    }

    setStatus("sending");
    setMessage("");

    try {
      await onSend(trimmedEmail);
      setStatus("sent");
      setMessage("Check inbox.");
    } catch (error) {
      const detail = error instanceof Error ? error.message : "Send failed.";
      setStatus("error");
      setMessage(detail);
    }
  }

  const isSending = status === "sending";
  const isDisabled = disabled || isSending;

  return (
    <form onSubmit={handleSubmit} className="auth-magic-form">
      <style>{magicLinkCss}</style>
      <div
        className={`auth-magic-field ${
          status === "error" ? "auth-magic-field--error" : ""
        }`}
      >
        <input
          aria-label="Email"
          type="email"
          value={email}
          disabled={isDisabled}
          onChange={(event) => {
            setEmail(event.target.value);
            if (status !== "sending") {
              setStatus("idle");
              setMessage("");
            }
          }}
          placeholder="Email for login"
        />
        <button
          type="submit"
          disabled={isDisabled}
          className="auth-magic-submit fintheon-black-liquid-glass fintheon-primary-action"
        >
          {isSending ? (
            <InProgressSpinner size={16} label="Sending login link" />
          ) : (
            <ArrowRight size={15} />
          )}
          <span>Log in</span>
        </button>
      </div>
      {message && (
        <p
          className={`auth-magic-message ${
            status === "error"
              ? "auth-magic-message--error"
              : "auth-magic-message--sent"
          }`}
        >
          {message}
        </p>
      )}
    </form>
  );
}

const magicLinkCss = `
  .auth-magic-form {
    display: grid;
    width: 100%;
    gap: 0.42rem;
  }

  .auth-magic-field {
    display: flex;
    min-height: 54px;
    overflow: visible;
    border: 1px solid color-mix(in srgb, var(--fintheon-primary, var(--fintheon-accent)) 13%, transparent);
    border-radius: 999px;
    background: color-mix(in srgb, var(--fintheon-bg, #050402) 88%, transparent);
    box-shadow:
      inset 0 1px 0 color-mix(in srgb, var(--fintheon-text, #f0ead6) 6%, transparent),
      0 10px 22px rgba(0, 0, 0, 0.16);
    backdrop-filter: blur(10px) saturate(1.04);
    -webkit-backdrop-filter: blur(10px) saturate(1.04);
    transition:
      border-color 180ms cubic-bezier(0.22, 1, 0.36, 1),
      background 180ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .auth-magic-field:focus-within {
    border-color: color-mix(in srgb, var(--fintheon-primary, var(--fintheon-accent)) 42%, transparent);
    background: color-mix(in srgb, var(--fintheon-bg, #050402) 80%, var(--fintheon-primary, var(--fintheon-accent)) 20%);
  }

  .auth-magic-field--error {
    border-color: color-mix(in srgb, #fca5a5 52%, transparent);
  }

  .auth-magic-field input {
    min-width: 0;
    flex: 1;
    border: 0;
    background: transparent;
    padding: 0 1.05rem;
    color: color-mix(in srgb, var(--fintheon-text, #f0ead6) 88%, transparent);
    font-size: 0.88rem;
    outline: none;
  }

  .auth-magic-field input::placeholder {
    color: color-mix(in srgb, var(--fintheon-text, #f0ead6) 36%, transparent);
  }

  .auth-magic-field input:disabled,
  .auth-magic-field button:disabled {
    cursor: not-allowed;
  }

  .auth-magic-submit {
    display: inline-flex;
    min-width: 7.2rem;
    margin: 4px;
    position: relative;
    isolation: isolate;
    overflow: visible;
    align-items: center;
    justify-content: center;
    gap: 0.42rem;
    border: 0;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 720;
    letter-spacing: 0;
    line-height: 1;
    color: color-mix(
      in srgb,
      var(--fintheon-text, #f0ead6) 88%,
      var(--fintheon-primary, var(--fintheon-accent)) 12%
    );
  }

  .auth-magic-submit > span {
    position: relative;
    z-index: 3;
    color: color-mix(
      in srgb,
      var(--fintheon-text, #f0ead6) 92%,
      var(--fintheon-primary, var(--fintheon-accent)) 8%
    );
    text-shadow:
      0 1px 1px rgba(0, 0, 0, 0.58),
      0 0 6px
      color-mix(
        in srgb,
        var(--fintheon-primary, var(--fintheon-accent)) 28%,
        transparent
      );
  }

  .auth-magic-submit > svg {
    position: relative;
    z-index: 3;
    color: color-mix(
      in srgb,
      var(--fintheon-text, #f0ead6) 92%,
      var(--fintheon-primary, var(--fintheon-accent)) 8%
    );
    filter:
      drop-shadow(0 1px 1px rgba(0, 0, 0, 0.58))
      drop-shadow(
        0 0 5px
        color-mix(
          in srgb,
          var(--fintheon-primary, var(--fintheon-accent)) 26%,
          transparent
        )
      );
  }

  .auth-magic-submit::before {
    z-index: 0 !important;
  }

  .auth-magic-submit::after {
    display: none !important;
    background: none !important;
    opacity: 0 !important;
  }

  .auth-magic-submit:disabled {
    opacity: 0.56;
  }

  .auth-magic-message {
    min-height: 1rem;
    margin: 0;
    text-align: center;
    font-size: 0.7rem;
    line-height: 1.2;
  }

  .auth-magic-message--sent {
    color: color-mix(in srgb, var(--fintheon-primary, var(--fintheon-accent)) 78%, transparent);
  }

  .auth-magic-message--error {
    color: color-mix(in srgb, #fca5a5 82%, transparent);
  }

  @media (max-width: 420px) {
    .auth-magic-submit {
      min-width: 6.2rem;
    }
  }
`;
